import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Clock, CheckCircle, XCircle, Eye, Edit, Trash2, RefreshCw, Package, BookOpen, Users, FileCheck, Layers, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { encryptId } from '../utils/urlEncryption';

const MySubmissions = ({ user }) => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState({
    orders: [],
    bookings: [],
    kitty_enrollments: [],
    approvals: [],
    total_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('all'); // all, orders, bookings, kitty_enrollments, approvals
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending
  const [dateFilter, setDateFilter] = useState('30days'); // today, 7days, 30days, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Type badge configuration for visual distinction
  const typeBadgeConfig = {
    orders: { icon: Package, label: 'Order', color: 'bg-blue-100 text-blue-700 border-blue-300' },
    bookings: { icon: BookOpen, label: 'Booking', color: 'bg-purple-100 text-purple-700 border-purple-300' },
    kitty_enrollments: { icon: Users, label: 'Kitty', color: 'bg-pink-100 text-pink-700 border-pink-300' },
    approvals: { icon: FileCheck, label: 'Approval', color: 'bg-gray-100 text-gray-700 border-gray-300' }
  };

  useEffect(() => {
    loadMySubmissions();
  }, []);

  const loadMySubmissions = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/submissions/my-all');
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load your submissions');
    } finally {
      setLoading(false);
    }
  };

  // Count items per module
  const getCount = (items) => (items || []).length;

  const handleDelete = async (itemId, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      const endpoints = {
        'orders': `/orders/${itemId}`,
        'bookings': `/bookings/${itemId}`,
        'kitty_enrollments': `/kitty/enrollments/${itemId}`,
        'approvals': `/approvals/${itemId}`
      };
      
      await axiosInstance.delete(endpoints[type]);
      toast.success(`${type.slice(0, -1)} deleted successfully`);
      loadMySubmissions();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    }
  };

  const handleWithdrawRevision = async (itemId, type) => {
    if (!window.confirm('Are you sure you want to withdraw your pending changes?')) return;

    try {
      const module = type === 'orders' ? 'order' : 'booking';
      await axiosInstance.post(`/admin/reject-revision/${module}/${itemId}`, {
        reason: 'Withdrawn by staff member'
      });
      toast.success('Pending changes withdrawn');
      loadMySubmissions();
    } catch (error) {
      console.error('Error withdrawing revision:', error);
      toast.error('Failed to withdraw changes');
    }
  };

  const getStatusBadge = (item) => {
    // Check for pending revision first (takes precedence)
    if (item.revision_status === 'pending' && item.revision_requested_by === user?.username) {
      return (
        <span className="flex items-center gap-1 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-bold">
          <Clock className="w-3 h-3" />
          Edit Pending Review
        </span>
      );
    }
    
    // Check for rejected revision
    if (item.revision_status === 'rejected' && item.revision_rejected_at) {
      return (
        <span className="flex items-center gap-1 bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-bold">
          <XCircle className="w-3 h-3" />
          Edit Rejected
        </span>
      );
    }
    
    const status = item.approval_status || 'approved';
    
    if (status === 'pending_approval') {
      return (
        <span className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-bold">
          <Clock className="w-3 h-3" />
          Pending Approval
        </span>
      );
    } else if (status === 'approved') {
      return (
        <span className="flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold">
          <CheckCircle className="w-3 h-3" />
          Approved
        </span>
      );
    } else if (status === 'rejected') {
      return (
        <span className="flex items-center gap-1 bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-bold">
          <XCircle className="w-3 h-3" />
          Rejected
        </span>
      );
    }
    return null;
  };

  // Get type badge component for each item type
  const getTypeBadge = (type) => {
    const config = typeBadgeConfig[type];
    if (!config) return null;
    const IconComponent = config.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${config.color}`}>
        <IconComponent className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Combine items when "all" is selected, or get single module items
  const getAllItems = () => {
    if (moduleFilter === 'all') {
      const allItems = [
        ...(submissions.orders || []).map(item => ({ ...item, _type: 'orders' })),
        ...(submissions.bookings || []).map(item => ({ ...item, _type: 'bookings' })),
        ...(submissions.kitty_enrollments || []).map(item => ({ ...item, _type: 'kitty_enrollments' })),
        ...(submissions.approvals || []).map(item => ({ ...item, _type: 'approvals' })),
      ];
      return allItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return (submissions[moduleFilter] || []).map(item => ({ ...item, _type: moduleFilter }));
  };

  const currentItems = getAllItems();
  
  const filteredItems = currentItems.filter(item => {
    // Status filter
    if (statusFilter !== 'all') {
      // Include items with pending revisions in "pending" filter
      const hasPendingRevision = item.revision_status === 'pending' && item.revision_requested_by === user?.username;
      
      if (statusFilter === 'pending' && item.approval_status !== 'pending_approval' && !hasPendingRevision) return false;
      if (statusFilter === 'approved' && item.approval_status !== 'approved' && !hasPendingRevision) return false;
      if (statusFilter === 'rejected' && item.approval_status !== 'rejected' && item.revision_status !== 'rejected') return false;
    }

    // Date filter
    const itemDate = new Date(item.created_at);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === 'today') {
      const itemDay = new Date(itemDate);
      itemDay.setHours(0, 0, 0, 0);
      if (itemDay.getTime() !== today.getTime()) return false;
    } else if (dateFilter === '7days') {
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      if (itemDate < sevenDaysAgo) return false;
    } else if (dateFilter === '30days') {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      if (itemDate < thirtyDaysAgo) return false;
    } else if (dateFilter === 'custom' && customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
      if (itemDate < startDate || itemDate > endDate) return false;
    }

    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-10 h-10 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">My Submissions</h1>
              <p className="text-gray-600 text-sm mt-1">Submissions pending approval across all modules</p>
            </div>
          </div>
          <button
            onClick={loadMySubmissions}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Summary Cards - Mobile Responsive */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-4">
          <div className="bg-white border-2 border-blue-200 rounded-lg p-2 sm:p-3 text-center">
            <Package className="w-5 h-5 mx-auto text-blue-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-blue-700">{getCount(submissions.orders)}</p>
            <p className="text-xs text-gray-600">Orders</p>
          </div>
          <div className="bg-white border-2 border-purple-200 rounded-lg p-2 sm:p-3 text-center">
            <BookOpen className="w-5 h-5 mx-auto text-purple-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-purple-700">{getCount(submissions.bookings)}</p>
            <p className="text-xs text-gray-600">Bookings</p>
          </div>
          <div className="bg-white border-2 border-pink-200 rounded-lg p-2 sm:p-3 text-center">
            <Users className="w-5 h-5 mx-auto text-pink-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-pink-700">{getCount(submissions.kitty_enrollments)}</p>
            <p className="text-xs text-gray-600">Kitty</p>
          </div>
          <div className="bg-white border-2 border-green-200 rounded-lg p-2 sm:p-3 text-center">
            <FileCheck className="w-5 h-5 mx-auto text-green-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-green-700">{getCount(submissions.approvals)}</p>
            <p className="text-xs text-gray-600">Approvals</p>
          </div>
          <div className="bg-white border-2 border-amber-200 rounded-lg p-2 sm:p-3 text-center">
            <Layers className="w-5 h-5 mx-auto text-amber-600 mb-1" />
            <p className="text-lg sm:text-xl font-bold text-amber-700">{
              getCount(submissions.orders) + 
              getCount(submissions.bookings) + 
              getCount(submissions.kitty_enrollments) + 
              getCount(submissions.approvals)
            }</p>
            <p className="text-xs text-gray-600">Total</p>
          </div>
        </div>

        {/* Module Filter - Dropdown Style */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Filter by Type</label>
            <select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none font-semibold"
            >
              <option value="all">All Submissions ({
                getCount(submissions.orders) + 
                getCount(submissions.bookings) + 
                getCount(submissions.kitty_enrollments) + 
                getCount(submissions.approvals)
              })</option>
              <option value="orders">Orders ({getCount(submissions.orders)})</option>
              <option value="bookings">Bookings ({getCount(submissions.bookings)})</option>
              <option value="kitty_enrollments">Kitty ({getCount(submissions.kitty_enrollments)})</option>
              <option value="approvals">Approvals ({getCount(submissions.approvals)})</option>
            </select>
          </div>

          {/* Status Filter - Dropdown Style */}
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Filter by Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none font-semibold"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending Approval</option>
            </select>
          </div>
        </div>

        {/* Date Filter - Mobile Optimized */}
        <div className="bg-white border-2 border-amber-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
          <h3 className="text-xs sm:text-sm font-bold text-gray-700 mb-2 sm:mb-3">📅 Filter by Date:</h3>
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
            <button
              onClick={() => setDateFilter('today')}
              className={`px-3 py-1.5 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                dateFilter === 'today'
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setDateFilter('7days')}
              className={`px-3 py-1.5 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                dateFilter === '7days'
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setDateFilter('30days')}
              className={`px-3 py-1.5 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                dateFilter === '30days'
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              30 Days
            </button>
            <button
              onClick={() => setDateFilter('custom')}
              className={`px-3 py-1.5 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                dateFilter === 'custom'
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Custom
            </button>
          </div>

          {/* Custom Date Range Inputs - Mobile Optimized */}
          {dateFilter === 'custom' && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-xs sm:text-sm"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-xs sm:text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading your orders...</p>
          </div>
        )}

        {/* Items List */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map(item => {
              const itemType = item._type; // Use _type which we added in getAllItems()
              const itemId = item.order_id || item.booking_id || item.stock_order_id || item.repair_id || item.enrollment_id || item.approval_id;
              const detailPath = itemType === 'orders' ? `/orders/${encryptId(itemId)}` :
                                itemType === 'bookings' ? `/bookings/${encryptId(itemId)}` :
                                itemType === 'approvals' ? `/approvals/${encryptId(itemId)}` :
                                `/kitty/enrollments/${encryptId(itemId)}`;
              const editPath = itemType === 'orders' ? `/orders/edit/${encryptId(itemId)}` :
                              itemType === 'bookings' ? `/bookings/edit/${encryptId(itemId)}` :
                              null;
              
              return (
                <div
                  key={`${itemType}-${itemId}`}
                  className="bg-white border-2 border-amber-200 rounded-xl p-4 sm:p-5 hover:shadow-xl transition-all"
                >
                  {/* Header with Type Badge */}
                  <div className="flex flex-col gap-2 mb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2 flex-wrap">
                        {getTypeBadge(itemType)}
                        <h3 className="text-base sm:text-lg font-bold text-gray-800">{itemId}</h3>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-500">
                        {new Date(item.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {getStatusBadge(item)}
                    </div>
                  </div>

                  {/* Item Info - Dynamic based on item._type */}
                  <div className="bg-blue-50 rounded-lg p-3 mb-3">
                    {itemType === 'orders' && (
                      <>
                        <p className="text-sm font-semibold text-gray-700">Customer: {item.customer_name || item.customer_id}</p>
                        <p className="text-xs text-gray-600">Items: {item.items?.length || 0}</p>
                      </>
                    )}
                    {itemType === 'bookings' && (
                      <>
                        <p className="text-sm font-semibold text-gray-700">Customer: {item.customer_name || item.customer_id}</p>
                        <p className="text-xs text-gray-600">Items: {item.items?.length || 0}</p>
                      </>
                    )}
                    {itemType === 'stock_orders' && (
                      <>
                        <p className="text-sm font-semibold text-gray-700">Staff: {item.staff_name}</p>
                        <p className="text-xs text-gray-600">Items: {item.items?.length || 0}</p>
                      </>
                    )}
                    {itemType === 'repairs' && (
                      <>
                        <p className="text-sm font-semibold text-gray-700">Customer: {item.customer_id}</p>
                        <p className="text-xs text-gray-600">Item: {item.item_name || item.item_type}</p>
                      </>
                    )}
                    {itemType === 'kitty_enrollments' && (
                      <>
                        <p className="text-sm font-semibold text-gray-700">Customer: {item.customer_id}</p>
                        <p className="text-xs text-gray-600">Scheme: {item.scheme_id}</p>
                      </>
                    )}
                    {itemType === 'approvals' && (
                      <>
                        <p className="text-sm font-semibold text-gray-700">Type: {item.approval_type || 'N/A'}</p>
                        <p className="text-xs text-gray-600">Entity: {item.entity_id || 'N/A'}</p>
                      </>
                    )}
                    <p className="text-xs text-gray-600">Status: {item.status || 'Pending'}</p>
                  </div>

                  {/* Rejection Reason */}
                  {item.approval_status === 'rejected' && item.rejection_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
                      <p className="text-xs font-semibold text-red-700">Rejection Reason:</p>
                      <p className="text-xs text-red-600">{item.rejection_reason}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => navigate(detailPath)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-semibold text-sm min-w-[80px]"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                    
                    {/* Show Withdraw button for pending revisions */}
                    {item.revision_status === 'pending' && item.revision_requested_by === user?.username && (
                      <button
                        onClick={() => handleWithdrawRevision(itemId, itemType)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 font-semibold text-sm min-w-[80px]"
                      >
                        <XCircle className="w-4 h-4" />
                        Withdraw
                      </button>
                    )}
                    
                    {item.approval_status === 'pending_approval' && (
                      <>
                        {editPath && (
                          <button
                            onClick={() => navigate(editPath)}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 font-semibold text-sm min-w-[80px]"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(itemId, itemType)}
                          className="flex items-center justify-center px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No {moduleFilter === 'all' ? 'submissions' : moduleFilter.replace('_', ' ')} found</p>
            <p className="text-gray-500 text-sm mt-2">Try adjusting your filters or date range</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubmissions;
